import { useState } from 'react'
import { requestSubscription } from '../../lib/emailSubscription'
import { trackEvent } from '../../lib/analytics'
import { useCommonT } from '../../i18n'

/**
 * Anonymous opt-in line. Success copy says "check your inbox", never "you're
 * subscribed" - the row does not exist until the confirmation link is tapped,
 * and promising otherwise would be a lie on the one screen where trust matters.
 *
 * Deliberately quiet: this sits above the footer on every route, so it dresses
 * like the footer (small, gray, borderless) rather than like a marketing card.
 * The old raised `surface` card with a bold headline and a filled purple
 * button read as an interstitial and got called out for it.
 */
export default function SubscribeBox({ source, compact = false }: {
  source: 'blog' | 'footer' | 'home' | 'guides'
  compact?: boolean
}) {
  const [email, setEmail] = useState('')
  const t = useCommonT()
  // eslint-disable-next-line i18next/no-literal-string -- state code
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'invalid' | 'error'>('idle')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (state === 'sending') return
    // eslint-disable-next-line i18next/no-literal-string -- state code
    setState('sending')
    const result = await requestSubscription(email, source)
    if (result === 'sent') {
      trackEvent('email_subscribe_requested', { source })
      // eslint-disable-next-line i18next/no-literal-string -- state code
      setState('sent')
      setEmail('')
    } else {
      setState(result)
    }
  }

  if (state === 'sent') {
    return (
      <div className="text-center" role="status">
        <p className="text-xs text-gray-400">{t('subscribe.sent')}</p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className={`text-center ${compact ? 'space-y-2' : 'space-y-3'}`}>
      <p className="text-xs text-gray-500">{t('subscribe.prompt')}</p>
      <div className="flex justify-center gap-2">
        <label htmlFor={`subscribe-${source}`} className="sr-only">{t('subscribe.email_label')}</label>
        <input
          id={`subscribe-${source}`}
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={e => { setEmail(e.target.value); if (state !== 'idle') setState('idle') }}
          placeholder={t('subscribe.placeholder')}
          // 16px min: anything smaller zooms the viewport on iOS Safari.
          style={{ fontSize: '16px' }}
          className="w-52 min-w-0 bg-transparent border border-gray-800 rounded-lg px-3 py-1.5 text-gray-300 placeholder-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
        />
        <button
          type="submit"
          disabled={state === 'sending'}
          className="px-3 py-1.5 rounded-lg border border-gray-800 text-xs text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-40 transition-colors whitespace-nowrap"
        >
          {state === 'sending' ? t('subscribe.sending') : t('subscribe.submit')}
        </button>
      </div>
      {state === 'invalid' && (
        <p className="text-xs text-amber-400" role="alert">{t('subscribe.invalid')}</p>
      )}
      {state === 'error' && (
        <p className="text-xs text-red-400" role="alert">{t('subscribe.error')}</p>
      )}
    </form>
  )
}
